import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    TableContainer,
    useDisclosure,
    Text,
    Table,
    Tbody,
    Tr,
    Td,
    Th,
    Thead,
    Spinner,
    IconButton,
} from '@chakra-ui/react';
import { EditIcon, AddIcon } from '@chakra-ui/icons';
import Header from '../../../components/header/Header';
import { useData } from '../../../data';
import { useSelector } from 'react-redux';
import PermissionBlinder from '../../../components/PermissionBlinder';
import CreatePlanModal from './CreatePlanModal';
import EditPlanModal from './EditPlanModal';

const ManageSubscriptionPlans = () => {
    const loggedInUser = useSelector((state) => state?.user?.userInfo);
    const { data, isLoading, refetch } = useData('subscription/plans');
    const { data: userTypes } = useData("user-types");
    const permissions = useSelector(
        (state) => state?.user?.userInfo?.permissions,
    );
    const { isOpen: createModalIsOpen, onToggle: onToggleCreateModal } =
        useDisclosure();
    const { isOpen: editModalIsOpen, onToggle: onToggleEditModal } =
        useDisclosure();
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [plans, setPlans] = useState([]);

    useEffect(() => {
        if (data?.data) {
            setPlans(Array.isArray(data.data) ? data.data : []);
        }
    }, [data]);

    const handleCreateSuccess = () => {
        refetch();
        onToggleCreateModal();
    };

    const handleEdit = (plan) => {
        setSelectedPlan(plan);
        onToggleEditModal();
    };

    const handleEditSuccess = () => {
        refetch();
        onToggleEditModal();
        setSelectedPlan(null);
    };

    const handleEditClose = () => {
        onToggleEditModal();
        setSelectedPlan(null);
    };

    const getUserTypeName = (userTypeId) => {
        if (!userTypes?.data?.userTypes || !userTypeId) return 'N/A';
        const userType = userTypes.data.userTypes.find(ut => ut.id === userTypeId);
        return userType ? userType.title.charAt(0).toUpperCase() + userType.title.slice(1) : 'N/A';
    };

    if (permissions && !permissions?.can_manage_subscriptions) {
        return <PermissionBlinder />;
    }
    return (
        <>
            <Box>
                <Header title="Manage Subscription Plans" />
                {createModalIsOpen && (
                    <CreatePlanModal
                        onToggle={onToggleCreateModal}
                        onSuccess={handleCreateSuccess}
                        userTypes={userTypes?.data?.userTypes || []}
                    />
                )}
                {editModalIsOpen && selectedPlan && (
                    <EditPlanModal
                        plan={selectedPlan}
                        onToggle={handleEditClose}
                        onSuccess={handleEditSuccess}
                    />
                )}
                {!createModalIsOpen && !editModalIsOpen && (
                    <>
                        <Box px={15} py={5}>
                            <Flex
                                w="100%"
                                justifyContent="space-between"
                                alignItems="center"
                                mb={4}
                            >
                                <Text fontSize="18px" fontWeight={600} color="brand.primary">
                                    All Subscription Plans
                                </Text>
                                <Button
                                    leftIcon={<AddIcon />}
                                    onClick={onToggleCreateModal}
                                    bg="gray.100"
                                    borderWidth="1px"
                                    borderColor="brand.primary"
                                    color="brand.primary"
                                    borderRadius="8px"
                                    minW="150px"
                                    _hover={{
                                        bg: 'gray.200',
                                        borderColor: 'brand.dark',
                                    }}
                                    fontWeight={500}
                                >
                                    Create New Plan
                                </Button>
                            </Flex>

                            <TableContainer
                                mt="30px"
                                borderRadius="10px"
                                overflowX="auto"
                                w="full"
                                bg="white"
                                boxShadow="md"
                            >
                                <Table variant="simple" size="md">
                                    <Thead bg="rgba(19, 74, 112, 0.05)">
                                        <Tr>
                                            <Th color="brand.primary" fontWeight={600}>
                                                Plan Name
                                            </Th>
                                            <Th color="brand.primary" fontWeight={600}>
                                                Price
                                            </Th>
                                            <Th color="brand.primary" fontWeight={600}>
                                                Billing Cycle
                                            </Th>
                                            <Th color="brand.primary" fontWeight={600}>
                                                User Type
                                            </Th>
                                            <Th color="brand.primary" fontWeight={600}>
                                                Description
                                            </Th>
                                            <Th color="brand.primary" fontWeight={600}>
                                                Actions
                                            </Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {isLoading ? (
                                            <Tr>
                                                <Td colSpan={6} textAlign="center" py={8}>
                                                    <Spinner size="lg" color="brand.primary" />
                                                </Td>
                                            </Tr>
                                        ) : plans.length === 0 ? (
                                            <Tr>
                                                <Td colSpan={6} textAlign="center" py={8}>
                                                    <Text color="gray.500">No subscription plans found</Text>
                                                </Td>
                                            </Tr>
                                        ) : (
                                            plans.map((plan) => (
                                                <Tr
                                                    key={plan.id}
                                                    _hover={{
                                                        bg: 'rgba(19, 74, 112, 0.02)',
                                                    }}
                                                >
                                                    <Td fontWeight={600} color="brand.primary">
                                                        {plan.name || 'N/A'}
                                                    </Td>
                                                    <Td>
                                                        ${parseFloat(plan.price || 0).toFixed(2)}
                                                    </Td>
                                                    <Td textTransform="capitalize">
                                                        {plan.billing_cycle || 'N/A'}
                                                    </Td>
                                                    <Td textTransform="capitalize">
                                                        {getUserTypeName(plan.user_type_id)}
                                                    </Td>
                                                    <Td maxW="300px">
                                                        <Text
                                                            noOfLines={2}
                                                            fontSize="14px"
                                                            color="gray.600"
                                                        >
                                                            {plan.description || 'No description'}
                                                        </Text>
                                                    </Td>
                                                    <Td>
                                                        <IconButton
                                                            icon={<EditIcon />}
                                                            aria-label="Edit plan"
                                                            size="sm"
                                                            colorScheme="blue"
                                                            variant="outline"
                                                            onClick={() => handleEdit(plan)}
                                                        />
                                                    </Td>
                                                </Tr>
                                            ))
                                        )}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        </Box>
                    </>
                )}
            </Box>
        </>
    );
};

export default ManageSubscriptionPlans;
